import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { RiskFormComponent } from '../../components/risk-form/risk-form.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';

import { Risk } from '../../../../models/entities/risk';

@Component({
  selector: 'app-risk-management-edit-page',
  imports: [LoaderComponent, RiskFormComponent],
  templateUrl: './risk-management-edit-page.component.html',
  styleUrl: './risk-management-edit-page.component.scss',
})
export class RiskManagementEditPageComponent implements OnInit, OnDestroy {

  riskId?: number;
  risk?: Risk;
  isEditMode: boolean = false;

  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  private readonly LOADER_CONTEXT = 'risk-management-edit-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService
  ) { }

  ngOnInit(): void {
    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('riskId');
    if (paramId) {
      this.riskId = Number(paramId);
      this.isEditMode = true;
      this.loadRisk(this.riskId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get code(): string {
    return this.risk?.code ?? '';
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Update Risk' : 'Create Risk';
  }

  onFormSavedData(risk: Risk): void {
    const isFirstSave = !this.isEditMode;

    this.risk = risk;
    this.riskId = risk.riskId ?? this.riskId;

    if (isFirstSave && this.riskId) {
      this.routeService.riskManagementDetail(Number(this.riskId));
    }
  }

  private loadRisk(riskId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailRisk(riskId).subscribe({
      next: (response: any) => {
        const risk: Risk | null = this.qualityManagementService.readResponseKey(response, 'risk') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!risk) {
          this.alertNotificationService.error('Risk not found.');
          return;
        }

        this.risk = risk;
      },
      error: (error: any) => {
        console.error('Error loading the risk:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.riskErrorMessage(error, 'Failed to load the risk.')
        );
      }
    });
  }
}
